import { InputAdornment, makeStyles } from '@material-ui/core'
import { groupBy, sortBy } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { titleCase } from 'title-case'
import equal from 'fast-deep-equal'

import {
  Form,
  FormikCheckboxGroup,
  FormikRadioGroup,
  FormikTextField,
  Typography,
  useIsTablet,
  mapToOptions,
} from 'src/modules/ui'

const useStyles = makeStyles(theme => ({
  allergenTable: {
    borderSpacing: 0,
  },
  allergenGroupName: {
    marginTop: theme.spacing(2),
  },
  topCols: {
    [theme.breakpoints.up('tablet')]: {
      display: 'flex',
      flexDirection: 'row',
    },
  },
  topCol: {
    marginBottom: `${theme.spacing(2)}px !important`,
    [theme.breakpoints.up('tablet')]: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      '&:first-child': {
        marginLeft: 0,
      },
      '&:last-child': {
        marginRight: 0,
      },
      width: `calc(50% - ${theme.spacing(2)}px)`,
    },
  },
}))

const Nutritional = ({
  allergenOptions,
  dietaryOptions,
  nutritionalOptions,
}) => {
  const classes = useStyles()
  const isTablet = useIsTablet()

  const allergenByGroup = groupBy(allergenOptions, a => a.group)
  const allergenGroups = Object.keys(allergenByGroup).map(group => ({
    group,
    items: sortBy(allergenByGroup[group], 'name'),
  }))
  const allergenValueOptions = [
    { value: '', label: 'Unspecified' },
    { value: 'does_not_contain', label: 'Does not contain' },
    { value: 'may_contain', label: 'May contain' },
    { value: 'contains', label: 'Contains' },
  ]

  return (
    <Form>
      <div className={classes.topCols}>
        <Form.InputGroup className={classes.topCol} title="Nutritional Info">
          {nutritionalOptions.map(({ id, name, units }, idx) => (
            <FormikTextField
              fullWidth
              key={idx}
              label={name}
              margin="normal"
              name={`nutritionalInfo.${id}`}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">{units}</InputAdornment>
                ),
              }}
            />
          ))}
        </Form.InputGroup>
        <Form.InputGroup className={classes.topCol} title="Dietary Info">
          <FormikCheckboxGroup
            name="dietaryInfo"
            options={dietaryOptions.map(mapToOptions)}
          />
        </Form.InputGroup>
      </div>
      <Form.InputGroup title="Allergen Info">
        <table className={classes.allergenTable}>
          <tbody>
            {allergenGroups.map(({ group, items }, idx) => (
              <React.Fragment key={idx}>
                <tr>
                  <td colSpan={4}>
                    <Typography
                      variant="h5"
                      className={classes.allergenGroupName}
                    >
                      {titleCase(group)}
                    </Typography>
                  </td>
                </tr>
                {items.map(({ id, name }, idx) => (
                  <FormikRadioGroup
                    fullWidth
                    key={idx}
                    label={name}
                    margin="normal"
                    name={`allergens.${id}`}
                    options={allergenValueOptions}
                    table={!isTablet}
                  />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </Form.InputGroup>
    </Form>
  )
}

Nutritional.propTypes = {
  allergenOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      group: PropTypes.string,
    })
  ),
  dietaryOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  nutritionalOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      units: PropTypes.string,
      position: PropTypes.number,
    })
  ),
}

export default React.memo(Nutritional, equal)
